import { useState, useEffect, useCallback, useRef } from "react";
import { scoutRequestService } from "../../services/scoutRequestService";

// Club recommendations inbox for one request (design 1d/1e). Card state is driven
// by the server's club_status (new | on_radar | dismissed) so a reload reflects
// truth; add-to-radar / dismiss / their undos are optimistic (flip club_status
// locally, revert on failure) for an instant, calm feel. Add-to-radar also fires
// the agent's "On the radar" server-side — the only feedback the agent ever gets
// (Invariant 2); dismiss produces zero agent-visible signal.
export const useRecommendationsInbox = (requestId) => {
  const [request, setRequest] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ count: 0, next: null, previous: null, currentPage: 1 });
  const abortRef = useRef(null);
  const viewedRef = useRef(new Set());

  const fetchInbox = useCallback(
    async (page = 1) => {
      if (!requestId) return;
      setLoading(true);
      setError(null);
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const params = new URLSearchParams();
      params.append("page", page);
      try {
        const [reqRes, recRes] = await Promise.all([
          scoutRequestService.getRequest(requestId, controller.signal),
          scoutRequestService.getRecommendations(requestId, params, controller.signal),
        ]);
        if (reqRes.success) setRequest(reqRes.data);
        if (recRes.success) {
          setRecommendations(recRes.data);
          setPagination(recRes.pagination);
        } else if (!reqRes.success) {
          setError(recRes.error || "Failed to load recommendations.");
        }
      } catch (err) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        setError("Failed to load recommendations. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [requestId],
  );

  useEffect(() => {
    fetchInbox(1);
  }, [fetchInbox]);

  const patchRec = (id, patch) =>
    setRecommendations((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  // Clear the "New" highlight once the club has actually seen a card. Fire-and-
  // forget, once per rec per session — a failed mark_viewed just leaves the pill.
  const markViewed = useCallback((rec) => {
    if (!rec?.is_new || viewedRef.current.has(rec.id)) return;
    viewedRef.current.add(rec.id);
    patchRec(rec.id, { is_new: false });
    scoutRequestService.markViewed(rec.id).then((res) => {
      if (!res.success) viewedRef.current.delete(rec.id);
    });
  }, []);

  const addToRadar = useCallback(async (rec) => {
    const prevStatus = rec.club_status;
    patchRec(rec.id, { club_status: "on_radar", on_radar: true, is_new: false });
    const res = await scoutRequestService.addToRadar(rec.id);
    if (!res.success) patchRec(rec.id, { club_status: prevStatus, on_radar: prevStatus === "on_radar" });
    return res;
  }, []);

  const undoAddToRadar = useCallback(async (rec) => {
    patchRec(rec.id, { club_status: "new", on_radar: false });
    const res = await scoutRequestService.undoAddToRadar(rec.id);
    if (!res.success) patchRec(rec.id, { club_status: "on_radar", on_radar: true });
    return res;
  }, []);

  const dismiss = useCallback(async (rec) => {
    const prevStatus = rec.club_status;
    patchRec(rec.id, { club_status: "dismissed", is_new: false });
    const res = await scoutRequestService.dismiss(rec.id);
    if (!res.success) patchRec(rec.id, { club_status: prevStatus });
    return res;
  }, []);

  const undoDismiss = useCallback(async (rec) => {
    patchRec(rec.id, { club_status: "new" });
    const res = await scoutRequestService.undoDismiss(rec.id);
    if (!res.success) patchRec(rec.id, { club_status: "dismissed" });
    return res;
  }, []);

  return {
    request,
    recommendations,
    loading,
    error,
    pagination,
    fetchInbox,
    markViewed,
    addToRadar,
    undoAddToRadar,
    dismiss,
    undoDismiss,
  };
};

export default useRecommendationsInbox;
